import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CalendarDays,
  Calendar,
  Check,
  ChevronLeft,
  ChevronDown,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { DatePickerSheet } from '@/components/DatePickerSheet';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { Difficulty, Tier } from '@/db/types';
import { getSystemMeta } from '@/data/systems';
import { parseLocalDate, toLocalYMD } from '@/lib/dates';
import { goBackOrTo } from '@/lib/nav';
import {
  addCustomTask,
  getActiveProperty,
  getSystemById,
} from '@/lib/tasks';
import { CUSTOM_TASK_ICONS, ICONS } from '@/lib/rule-icon';
import { webNoOutline } from '@/lib/web-style';

type CadencePreset = Tier | 'custom';

const TIER_OPTS: { v: CadencePreset; label: string; cadenceDays: number | null }[] = [
  { v: 'weekly', label: 'Every week', cadenceDays: 7 },
  { v: 'monthly', label: 'Every month', cadenceDays: 30 },
  { v: 'quarterly', label: 'Every 3 months', cadenceDays: 90 },
  { v: 'biannual', label: 'Twice a year', cadenceDays: 180 },
  { v: 'annual', label: 'Yearly', cadenceDays: 365 },
  { v: 'custom', label: 'Custom — pick days', cadenceDays: null },
];
const DIFF_OPTS: { v: Difficulty; label: string }[] = [
  { v: 'easy', label: 'Easy' },
  { v: 'moderate', label: 'Moderate' },
  { v: 'hard', label: 'Hard' },
];

export default function NewCustomTask() {
  const router = useRouter();
  const db = useSQLiteContext();
  const { systemId } = useLocalSearchParams<{ systemId: string }>();

  const [systemTitle, setSystemTitle] = useState<string>('');
  const [title, setTitle] = useState('');
  const [why, setWhy] = useState('');
  const [tier, setTier] = useState<CadencePreset | null>(null);
  const [customDays, setCustomDays] = useState('14');
  const [oneOff, setOneOff] = useState(false);
  // First-due date: YYYY-MM-DD string. Defaults to "in a week" so the
  // recurring case has a sensible cadence start; user can shift earlier
  // (e.g. "today" or "tomorrow") via presets or any explicit date.
  const [firstDue, setFirstDue] = useState<string>(() =>
    toLocalYMD(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );
  const [diff, setDiff] = useState<Difficulty>('easy');
  const [timeMin, setTimeMin] = useState('15');
  const [supplies, setSupplies] = useState('');
  const [steps, setSteps] = useState('');
  const [icon, setIcon] = useState('Sparkles');
  const [tierOpen, setTierOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // `systemId === 'none'` is the sentinel for a property-wide task with no
  // system attachment — the quick-action menu uses it.
  const hasSystem = !!systemId && systemId !== 'none';

  useEffect(() => {
    (async () => {
      if (!hasSystem || !systemId) return;
      const s = await getSystemById(db, systemId);
      if (s) {
        const meta = getSystemMeta(s.kind);
        setSystemTitle(s.nickname ?? meta?.label ?? s.kind);
        if (meta?.icon) setIcon(meta.icon);
      }
    })();
  }, [db, systemId, hasSystem]);

  const canSave = title.trim().length > 0;
  const tierOpt = TIER_OPTS.find((t) => t.v === tier);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      // Resolve cadence: custom uses the user-supplied days, otherwise the
      // tier's preset; one-off stays null.
      let cadenceDays: number | null = null;
      if (!oneOff) {
        if (tier === 'custom') {
          const parsed = parseInt(customDays, 10);
          if (!isNaN(parsed) && parsed > 0) cadenceDays = parsed;
        } else if (tierOpt?.cadenceDays) {
          cadenceDays = tierOpt.cadenceDays;
        }
      }
      // Prefer the explicit first-due date the user picked. Fall back to a
      // sensible auto-offset only if the string failed to parse.
      const parsedFirstDue = parseLocalDate(firstDue);
      const dueAt = !isNaN(parsedFirstDue)
        ? parsedFirstDue
        : Date.now() + (cadenceDays ? Math.min(cadenceDays, 30) : 7) * 24 * 60 * 60 * 1000;
      // Map the picker enum to the DB tier; custom rows store the tier as
      // null since the cadence_days column carries the truth.
      const dbTier: Tier | null =
        oneOff || tier === null || tier === 'custom' ? null : tier;
      await addCustomTask(db, {
        propertyId: prop.id,
        systemId: hasSystem ? systemId ?? null : null,
        title,
        why: why.trim() || null,
        time_minutes: parseInt(timeMin, 10) || null,
        difficulty: diff,
        icon,
        tier: dbTier,
        cadence_days: cadenceDays,
        due_at: dueAt,
        supplies: supplies
          .split(/\n/)
          .map((s) => s.trim())
          .filter(Boolean),
        steps: steps
          .split(/\n/)
          .map((s) => s.trim())
          .filter(Boolean),
      });
      goBackOrTo(router, '/(tabs)/schedule');
    } finally {
      setSaving(false);
    }
  };

  const IconPreview = ICONS[icon] ?? ICONS.Sparkles;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)/schedule')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Add a custom task</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 130 }} showsVerticalScrollIndicator={false}>
        {hasSystem && systemTitle ? (
          <View style={styles.context}>
            <Text style={styles.contextLabel}>FOR THIS SYSTEM</Text>
            <Text style={styles.contextValue}>{systemTitle}</Text>
          </View>
        ) : (
          <View style={[styles.context, { backgroundColor: Palette.honeySoft }]}>
            <Text style={[styles.contextLabel, { color: Palette.honeyDeep }]}>PROPERTY-WIDE TASK</Text>
            <Text style={[styles.contextValue, { color: '#3A2A06' }]}>
              Not tied to a specific system
            </Text>
          </View>
        )}

        <View style={styles.card}>
          <Text style={styles.cardTitle}>BASICS</Text>

          <Pressable
            onPress={() => setIconOpen(true)}
            style={({ pressed }) => [styles.iconPickRow, pressed && { opacity: 0.92 }]}>
            <View style={styles.iconPickBox}>
              <IconPreview size={22} color={Palette.forest} strokeWidth={1.7} />
            </View>
            <Text style={styles.iconPickText}>Tap to change icon</Text>
            <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
          </Pressable>

          <Field label="What needs doing?" value={title} onChangeText={setTitle} placeholder="e.g. Clean the koi pond filter" />
          <Field
            label="Why does it matter?"
            value={why}
            onChangeText={setWhy}
            placeholder="Optional. Helps future-you remember the point."
            multiline
          />
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>WHEN</Text>

          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.toggleLabel}>{oneOff ? 'One-time only' : 'Recurring'}</Text>
              <Text style={styles.toggleSub}>
                {oneOff
                  ? "Won't repeat after you mark it done."
                  : 'Plants a fresh reminder at the cadence you pick.'}
              </Text>
            </View>
            <Pressable
              onPress={() => setOneOff(!oneOff)}
              style={({ pressed }) => [styles.toggle, !oneOff && styles.toggleOn, pressed && { opacity: 0.85 }]}>
              <View style={[styles.toggleThumb, !oneOff && styles.toggleThumbOn]} />
            </Pressable>
          </View>

          <FirstDueRow value={firstDue} onChange={setFirstDue} oneOff={oneOff} />

          {!oneOff ? (
            <>
              <Pressable
                onPress={() => setTierOpen(true)}
                style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }, { marginTop: 12 }]}>
                <Text style={[styles.pickText, !tier && styles.pickPlaceholder]}>
                  {tier === 'custom'
                    ? `Every ${customDays || '?'} day${customDays === '1' ? '' : 's'}`
                    : tierOpt?.label ?? 'How often?'}
                </Text>
                <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
              </Pressable>
              {tier === 'custom' ? (
                <View style={{ marginTop: 10 }}>
                  <Text style={styles.fieldLabel}>EVERY HOW MANY DAYS?</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 5 }}>
                    <TextInput
                      value={customDays}
                      onChangeText={(v) => setCustomDays(v.replace(/[^0-9]/g, ''))}
                      placeholder="14"
                      placeholderTextColor={Palette.inkFaint}
                      inputMode="numeric"
                      style={[styles.fieldInput, webNoOutline, { flex: 1 }]}
                    />
                    <Text style={{ fontFamily: Fonts.sansBold, color: Palette.inkSoft, fontSize: 13 }}>
                      days
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    {[14, 21, 45, 60, 120, 180].map((n) => (
                      <Pressable
                        key={n}
                        onPress={() => setCustomDays(String(n))}
                        style={({ pressed }) => [
                          styles.quickChip,
                          pressed && { opacity: 0.85 },
                          customDays === String(n) && styles.quickChipSel,
                        ]}>
                        <Text
                          style={[
                            styles.quickChipText,
                            customDays === String(n) && styles.quickChipTextSel,
                          ]}>
                          {n}d
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              ) : null}
            </>
          ) : null}
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>EFFORT</Text>
          <Field label="Time (minutes)" value={timeMin} onChangeText={setTimeMin} placeholder="15" inputMode="numeric" />
          <Pressable
            onPress={() => setDiffOpen(true)}
            style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }, { marginTop: 12 }]}>
            <Text style={styles.pickText}>{DIFF_OPTS.find((d) => d.v === diff)?.label ?? 'Easy'}</Text>
            <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
          </Pressable>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>NOTES · OPTIONAL</Text>
          <Field
            label="Supplies (one per line)"
            value={supplies}
            onChangeText={setSupplies}
            placeholder={`Vacuum\nReplacement filter`}
            multiline
          />
          <Field
            label="Steps (one per line)"
            value={steps}
            onChangeText={setSteps}
            placeholder={`Shut off power\nRemove cover\nVacuum the coils`}
            multiline
          />
        </View>

      </ScrollView>

      <StickyBottomBar>
        <Pressable
          onPress={save}
          disabled={!canSave || saving}
          style={({ pressed }) => [
            styles.saveBtn,
            { flex: 1 },
            (!canSave || saving) && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{saving ? 'Saving…' : 'Save task'}</Text>
        </Pressable>
      </StickyBottomBar>

      {/* Tier picker */}
      <Modal animationType="fade" transparent visible={tierOpen} onRequestClose={() => setTierOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setTierOpen(false)}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>How often?</Text>
            {TIER_OPTS.map((o) => {
              const sel = tier === o.v;
              return (
                <Pressable
                  key={o.v}
                  onPress={() => {
                    setTier(o.v);
                    setTierOpen(false);
                  }}
                  style={({ pressed }) => [
                    styles.modalRow,
                    pressed && { backgroundColor: '#EEF3EE' },
                    sel && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.modalRowText}>{o.label}</Text>
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>

      {/* Difficulty picker */}
      <Modal animationType="fade" transparent visible={diffOpen} onRequestClose={() => setDiffOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setDiffOpen(false)}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>How hard?</Text>
            {DIFF_OPTS.map((o) => {
              const sel = diff === o.v;
              return (
                <Pressable
                  key={o.v}
                  onPress={() => {
                    setDiff(o.v);
                    setDiffOpen(false);
                  }}
                  style={({ pressed }) => [
                    styles.modalRow,
                    pressed && { backgroundColor: '#EEF3EE' },
                    sel && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.modalRowText}>{o.label}</Text>
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>

      {/* Icon picker — grouped by theme, ~140 icons */}
      <Modal animationType="fade" transparent visible={iconOpen} onRequestClose={() => setIconOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setIconOpen(false)}>
          <Pressable style={styles.modalCardWide} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Pick an icon</Text>
            <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={false}>
              {CUSTOM_TASK_ICONS.map((group) => (
                <View key={group.group} style={{ marginBottom: 14 }}>
                  <Text style={styles.iconGroupLabel}>{group.group.toUpperCase()}</Text>
                  <View style={styles.iconGrid}>
                    {group.keys
                      .filter((k) => ICONS[k])
                      .map((name) => {
                        const I = ICONS[name];
                        const sel = icon === name;
                        return (
                          <Pressable
                            key={name}
                            onPress={() => {
                              setIcon(name);
                              setIconOpen(false);
                            }}
                            style={({ pressed }) => [
                              styles.iconChoice,
                              sel && styles.iconChoiceSel,
                              pressed && { opacity: 0.85 },
                            ]}>
                            <I size={20} color={sel ? Palette.cream : Palette.forest} strokeWidth={1.7} />
                          </Pressable>
                        );
                      })}
                  </View>
                </View>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

function FirstDueRow({
  value,
  onChange,
  oneOff,
}: {
  value: string;
  onChange: (v: string) => void;
  oneOff: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const presets: { label: string; days: number }[] = [
    { label: 'Today', days: 0 },
    { label: 'Tomorrow', days: 1 },
    { label: 'In 3 days', days: 3 },
    { label: 'Next week', days: 7 },
    { label: 'In 2 weeks', days: 14 },
    { label: 'Next month', days: 30 },
  ];
  const pickPreset = (days: number) => {
    const d = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    onChange(toLocalYMD(d.getTime()));
  };
  const parsedMs = (() => {
    const t = parseLocalDate(value);
    return isNaN(t) ? null : t;
  })();
  return (
    <View style={{ marginTop: 12 }}>
      <Text style={styles.fieldLabel}>
        {oneOff ? 'WHEN IS IT DUE?' : 'WHEN DOES IT FIRST POP UP?'}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 5 }}>
        <CalendarDays size={16} color={Palette.inkSoft} strokeWidth={1.8} />
        <TextInput
          value={value}
          onChangeText={onChange}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={Palette.inkFaint}
          autoCapitalize="none"
          autoCorrect={false}
          style={[styles.fieldInput, { flex: 1, marginTop: 0 }]}
        />
        <Pressable
          onPress={() => setPickerOpen(true)}
          style={({ pressed }) => [styles.calBtn, pressed && { opacity: 0.85 }]}
          accessibilityLabel="Pick a date on the calendar">
          <Calendar size={16} color={Palette.forest} strokeWidth={1.8} />
        </Pressable>
      </View>
      <View style={{ flexDirection: 'row', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {presets.map((p) => (
          <Pressable
            key={p.label}
            onPress={() => pickPreset(p.days)}
            style={({ pressed }) => [
              styles.quickChip,
              pressed && { opacity: 0.85 },
            ]}>
            <Text style={styles.quickChipText}>{p.label}</Text>
          </Pressable>
        ))}
      </View>
      <DatePickerSheet
        open={pickerOpen}
        value={parsedMs}
        onPick={(ms) => {
          onChange(toLocalYMD(ms));
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
        title={oneOff ? 'Due date' : 'First due date'}
      />
    </View>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  inputMode = 'text',
  multiline,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
  multiline?: boolean;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        multiline={multiline}
        style={[styles.fieldInput, multiline && { height: 88, textAlignVertical: 'top' }]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  context: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    padding: 12, backgroundColor: '#DCE7E1',
    borderRadius: 12, gap: 2,
  },
  contextLabel: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.forestLight,
    letterSpacing: 0.5,
  },
  contextValue: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.forest },
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  cardTitle: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  pickPlaceholder: { color: Palette.inkFaint, fontFamily: Fonts.sansMedium },
  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 10 },
  toggleLabel: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  toggleSub: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    marginTop: 2, lineHeight: 17,
  },
  toggle: {
    width: 44, height: 26, borderRadius: 13,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleThumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: Palette.cream },
  toggleThumbOn: { transform: [{ translateX: 18 }] },
  iconPickRow: {
    marginTop: 10, flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 12, paddingVertical: 10,
    backgroundColor: Palette.sand, borderRadius: 11,
    borderWidth: 1.5, borderColor: Palette.line,
  },
  iconPickBox: {
    width: 38, height: 38, borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  iconPickText: { flex: 1, fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.ink },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: Radius.xl,
    padding: Spacing.four, width: '100%', maxWidth: 360,
  },
  modalCardWide: {
    backgroundColor: Palette.sand, borderRadius: Radius.xl,
    padding: Spacing.four, width: '100%', maxWidth: 420,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink,
    marginBottom: 10,
  },
  modalRow: { paddingVertical: 12, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  iconGrid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 8,
    paddingTop: 4,
  },
  iconChoice: {
    width: 44, height: 44, borderRadius: 12,
    backgroundColor: Palette.card, borderWidth: 1.5, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  iconChoiceSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  iconGroupLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  calBtn: {
    width: 38, height: 38, borderRadius: 11,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  quickChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  quickChipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  quickChipText: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.ink },
  quickChipTextSel: { color: Palette.cream },
});
